/**
 * Voice and video calls (WebRTC media) over Susurro's E2E signaling.
 *
 * Zero-knowledge: the signaling (SDP offer/answer + ICE candidates) does NOT go
 * through a cleartext room, but as control messages **ratcheted** by the
 * 1:1 session (see `engine.sendCallSignal`). The relay only sees the opaque
 * ciphertext of the envelope: never the SDP nor the DTLS fingerprint. Since the SDP (which carries
 * the DTLS-SRTP fingerprint that authenticates the media keys) travels over the channel
 * bound to the X3DH identities, the P2P media is verified with the identity
 * keys — the same security number as the chat covers the call.
 *
 * `RTCPeerConnection` and `getUserMedia` are INJECTABLE: in production they are the
 * browser globals; in test a fake pair is injected to test the state
 * machine and signal routing without a browser.
 */

export type CallMediaKind = "audio" | "video";

export type CallState =
  | "idle"
  | "calling" // saliente: oferta enviada, esperando respuesta
  | "incoming" // entrante: oferta recibida, esperando aceptar/rechazar
  | "connecting" // respuesta intercambiada, negociando ICE
  | "connected" // media fluyendo
  | "ended";

export type CallSignal =
  | {
      readonly kind: "offer";
      readonly callId: string;
      readonly media: CallMediaKind;
      readonly sdp: string;
    }
  | { readonly kind: "answer"; readonly callId: string; readonly sdp: string }
  | { readonly kind: "ice"; readonly callId: string; readonly candidate: string }
  | { readonly kind: "hangup"; readonly callId: string; readonly reason?: string };

/** Structural subset of `RTCPeerConnection` that we use (to inject a fake). */
export interface PeerConnectionLike {
  connectionState: string;
  addEventListener(type: string, listener: (ev: unknown) => void): void;
  addTrack(track: MediaStreamTrack, stream: MediaStream): unknown;
  createOffer(): Promise<RTCSessionDescriptionInit>;
  createAnswer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(desc: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(desc: RTCSessionDescriptionInit): Promise<void>;
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>;
  close(): void;
}

interface CallDeps {
  readonly selfDeviceId: string;
  /** Sends a call signal to the peer (through the ratchet → blind relay). */
  readonly send: (peer: string, signal: CallSignal) => Promise<void> | void;
  readonly createPeerConnection: () => PeerConnectionLike;
  readonly getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  readonly onStateChange?: (state: CallState, info: CallInfo) => void;
  readonly onLocalStream?: (stream: MediaStream) => void;
  readonly onRemoteStream?: (stream: MediaStream) => void;
  readonly genId?: () => string;
}

export interface CallInfo {
  readonly peerDeviceId?: string;
  readonly callId?: string;
  readonly media: CallMediaKind;
  readonly isCaller: boolean;
  readonly muted: boolean;
  readonly cameraOff: boolean;
}

function randomId(): string {
  return Math.random().toString(36).slice(2, 12);
}

function readProp(ev: unknown, key: string): unknown {
  return typeof ev === "object" && ev !== null ? Reflect.get(ev, key) : undefined;
}

/** VALIDATED parsing of a serialized SDP (without `as`; it comes from decrypted network). */
export function parseSessionDescription(json: string): RTCSessionDescriptionInit {
  const v: unknown = JSON.parse(json);
  const type = readProp(v, "type");
  const sdp = readProp(v, "sdp");
  if (
    (type === "offer" || type === "answer" || type === "pranswer" || type === "rollback") &&
    (typeof sdp === "string" || sdp === undefined)
  ) {
    return { type, sdp };
  }
  throw new Error("call: invalid session description (SDP)");
}

/** VALIDATED parsing of a serialized ICE candidate (without `as`). */
export function parseIceCandidate(json: string): RTCIceCandidateInit {
  const v: unknown = JSON.parse(json);
  const candidate = readProp(v, "candidate");
  const sdpMid = readProp(v, "sdpMid");
  const sdpMLineIndex = readProp(v, "sdpMLineIndex");
  const usernameFragment = readProp(v, "usernameFragment");
  return {
    candidate: typeof candidate === "string" ? candidate : "",
    sdpMid: typeof sdpMid === "string" ? sdpMid : undefined,
    sdpMLineIndex: typeof sdpMLineIndex === "number" ? sdpMLineIndex : undefined,
    usernameFragment: typeof usernameFragment === "string" ? usernameFragment : undefined,
  };
}

export class CallManager {
  private state: CallState = "idle";
  private pc?: PeerConnectionLike;
  private localStream?: MediaStream;
  private peerDeviceId?: string;
  private callId?: string;
  private media: CallMediaKind = "audio";
  private isCaller = false;
  private muted = false;
  private cameraOff = false;
  private pendingOffer?: CallSignal & { kind: "offer" };
  private remoteReady = false;
  private readonly iceQueue: RTCIceCandidateInit[] = [];
  private readonly genId: () => string;

  constructor(private readonly deps: CallDeps) {
    this.genId = deps.genId ?? randomId;
  }

  getState(): CallState {
    return this.state;
  }

  getInfo(): CallInfo {
    return {
      peerDeviceId: this.peerDeviceId,
      callId: this.callId,
      media: this.media,
      isCaller: this.isCaller,
      muted: this.muted,
      cameraOff: this.cameraOff,
    };
  }

  private setState(state: CallState): void {
    this.state = state;
    this.deps.onStateChange?.(state, this.getInfo());
  }

  // --- Outgoing -------------------------------------------------------------

  async startCall(peerDeviceId: string, media: CallMediaKind): Promise<void> {
    if (this.state !== "idle" && this.state !== "ended") {
      throw new Error("call: a call is already in progress");
    }
    this.reset();
    this.peerDeviceId = peerDeviceId;
    this.callId = this.genId();
    this.media = media;
    this.isCaller = true;
    this.setState("calling");

    const pc = this.buildPeerConnection();
    await this.acquireLocalMedia(media);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await this.deps.send(peerDeviceId, {
      kind: "offer",
      callId: this.callId,
      media,
      sdp: JSON.stringify(offer),
    });
  }

  // --- Incoming -------------------------------------------------------------

  /** Entry point of every signal received from the peer (the engine calls it). */
  async handleSignal(peer: string, signal: CallSignal): Promise<void> {
    switch (signal.kind) {
      case "offer": {
        await this.onOffer(peer, signal);
        break;
      }
      case "answer": {
        await this.onAnswer(signal);
        break;
      }
      case "ice": {
        await this.onIce(signal);
        break;
      }
      case "hangup": {
        this.onHangup(signal);
        break;
      }
    }
  }

  private async onOffer(peer: string, signal: CallSignal & { kind: "offer" }): Promise<void> {
    if (this.state !== "idle" && this.state !== "ended") {
      // Busy: reject without disturbing the call in progress.
      await this.deps.send(peer, { kind: "hangup", callId: signal.callId, reason: "busy" });
      return;
    }
    this.reset();
    this.peerDeviceId = peer;
    this.callId = signal.callId;
    this.media = signal.media;
    this.isCaller = false;
    this.pendingOffer = signal;
    this.setState("incoming");
  }

  /** The recipient accepts: acquires media, answers and starts the negotiation. */
  async accept(): Promise<void> {
    if (this.state !== "incoming" || !this.pendingOffer || !this.peerDeviceId) {
      return;
    }
    this.setState("connecting");
    const pc = this.buildPeerConnection();
    await this.acquireLocalMedia(this.media);
    await pc.setRemoteDescription(parseSessionDescription(this.pendingOffer.sdp));
    this.remoteReady = true;
    await this.flushIce();
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await this.deps.send(this.peerDeviceId, {
      kind: "answer",
      callId: this.callId!,
      sdp: JSON.stringify(answer),
    });
  }

  async reject(): Promise<void> {
    if (this.peerDeviceId && this.callId) {
      await this.deps.send(this.peerDeviceId, {
        kind: "hangup",
        callId: this.callId,
        reason: "rejected",
      });
    }
    this.teardown();
  }

  private async onAnswer(signal: CallSignal & { kind: "answer" }): Promise<void> {
    if (!this.pc || !this.isCaller || signal.callId !== this.callId) {
      return;
    }
    await this.pc.setRemoteDescription(parseSessionDescription(signal.sdp));
    this.remoteReady = true;
    this.setState("connecting");
    await this.flushIce();
  }

  private async onIce(signal: CallSignal & { kind: "ice" }): Promise<void> {
    if (!this.pc || signal.callId !== this.callId) {
      return;
    }
    const candidate = parseIceCandidate(signal.candidate);
    if (this.remoteReady) {
      await this.pc.addIceCandidate(candidate);
    } else {
      this.iceQueue.push(candidate);
    }
  }

  private onHangup(signal: CallSignal & { kind: "hangup" }): void {
    if (signal.callId !== this.callId) {
      return;
    }
    this.teardown();
  }

  /** Hangs up the active call and notifies the peer. */
  async hangup(): Promise<void> {
    if (this.peerDeviceId && this.callId && this.state !== "idle") {
      await this.deps.send(this.peerDeviceId, { kind: "hangup", callId: this.callId });
    }
    this.teardown();
  }

  // --- Local media ----------------------------------------------------------

  toggleMute(): boolean {
    this.muted = !this.muted;
    this.localStream?.getAudioTracks().forEach((t) => (t.enabled = !this.muted));
    this.deps.onStateChange?.(this.state, this.getInfo());
    return this.muted;
  }

  toggleCamera(): boolean {
    this.cameraOff = !this.cameraOff;
    this.localStream?.getVideoTracks().forEach((t) => (t.enabled = !this.cameraOff));
    this.deps.onStateChange?.(this.state, this.getInfo());
    return this.cameraOff;
  }

  // --- Internals ------------------------------------------------------------

  private buildPeerConnection(): PeerConnectionLike {
    const pc = this.deps.createPeerConnection();
    this.pc = pc;
    pc.addEventListener("icecandidate", (ev) => {
      const candidate = readProp(ev, "candidate");
      if (candidate && this.peerDeviceId && this.callId) {
        // JSON.stringify invokes RTCIceCandidate.toJSON automatically.
        void this.deps.send(this.peerDeviceId, {
          kind: "ice",
          callId: this.callId,
          candidate: JSON.stringify(candidate),
        });
      }
    });
    pc.addEventListener("track", (ev) => {
      const streams = readProp(ev, "streams");
      const track = readProp(ev, "track");
      let stream: MediaStream | undefined;
      if (Array.isArray(streams) && streams[0] instanceof MediaStream) {
        stream = streams[0];
      } else if (track instanceof MediaStreamTrack) {
        stream = new MediaStream([track]);
      }
      if (stream) {
        this.deps.onRemoteStream?.(stream);
      }
    });
    pc.addEventListener("connectionstatechange", () => {
      if (pc.connectionState === "connected") {
        this.setState("connected");
      } else if (
        pc.connectionState === "failed" ||
        pc.connectionState === "disconnected" ||
        pc.connectionState === "closed"
      ) {
        if (this.state === "connected" || this.state === "connecting") {
          this.teardown();
        }
      }
    });
    return pc;
  }

  private async acquireLocalMedia(media: CallMediaKind): Promise<void> {
    const stream = await this.deps.getUserMedia({ audio: true, video: media === "video" });
    this.localStream = stream;
    this.deps.onLocalStream?.(stream);
    for (const track of stream.getTracks()) {
      this.pc?.addTrack(track, stream);
    }
  }

  private async flushIce(): Promise<void> {
    if (!this.pc) {
      return;
    }
    while (this.iceQueue.length > 0) {
      const candidate = this.iceQueue.shift()!;
      await this.pc.addIceCandidate(candidate);
    }
  }

  private teardown(): void {
    this.setState("ended");
    this.reset();
    this.setState("idle");
  }

  private reset(): void {
    try {
      this.pc?.close();
    } catch {
      /* ignores double closes */
    }
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.pc = undefined;
    this.localStream = undefined;
    this.pendingOffer = undefined;
    this.remoteReady = false;
    this.iceQueue.length = 0;
    this.muted = false;
    this.cameraOff = false;
    // peerDeviceId/callId/media/isCaller are kept until the next start
    // (so the "ended" UI shows who it was with) — they are overwritten in start/onOffer.
  }
}
